import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import { useDroppable } from '@dnd-kit/core'
import { TaskEntry } from '../../../../components/TaskEntry'
import type { TaskCreationData } from '../../../../components/TaskEntry'
import type { Task } from '../../../../../../lib/db/models'
import { taskToEntryData } from '../../../../utils/taskToEntryData'
import { Dot } from '../../../../../../components/ui/Dot'
import { dateKey } from '../../hooks/upcomingTasks.utils'
import { DraggableTaskEntry } from '../DraggableTaskEntry'
import './DayGroup.scss'

interface DayGroupProps {
  date: Date
  tasks: Task[]
  /** Day-key of today (from the parent, driven by useMidnightRefresh). When
   *  this matches `dateKey(date)`, the day number uses the accent colour. */
  todayDateKey: string
  /** ID of the task currently in expanded/edit mode — ONLY for this day.
   *  Parent filters: other days receive `null` so their memo hits. */
  editingTaskId: string | null
  /** ID of the task currently highlighted — ONLY for this day. */
  selectedTaskId: string | null
  /** True when this specific day is in "create new task" mode. */
  isCreating: boolean
  onStartEdit: (taskId: string) => void
  onCancelEdit: () => void
  onNavigateEdit: (taskId: string, direction: 'next' | 'prev') => void
  onSelectTask: (taskId: string) => void
  onStartCreate: (dateKey: string) => void
  onCancelCreate: () => void
  onSubmitCreate: (dateKey: string, data: TaskCreationData) => void
  onToggleComplete: (taskId: string) => void
  onDelete: (taskId: string) => void
  onUpdateTask: (taskId: string, data: TaskCreationData) => void
  onDuplicate?: (taskId: string) => void
  onContextMenuBadgeUpdate?: (clickedTaskId: string, updates: Partial<Omit<Task, 'id'>>) => void
}


/**
 * Tiny isolation wrapper for `useDroppable`.
 *
 * The dnd-kit `useDroppable` hook subscribes to DndContext, and DndContext
 * fires updates on *every* cursor move during a drag. Hosting that hook
 * directly inside DayGroup made every DndContext update bypass the outer
 * React.memo and re-render the entire DayGroup tree (TaskEntries, framer-
 * motion, badge dropdowns) for every droppable on the page (~120 of them
 * across 4 visible months). Profiling showed ~1200 DayGroup re-renders per
 * single drag-and-drop operation.
 *
 * Splitting the droppable into its own component means DndContext updates
 * only re-render this thin wrapper, while the heavy DayGroup body stays
 * memoized.
 */
interface DayDropZoneProps {
  dayKey: string
  className: string
  children: React.ReactNode
}

const DayDropZone: React.FC<DayDropZoneProps> = ({ dayKey, className, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id: dayKey })
  return (
    <div
      ref={setNodeRef}
      className={`${className}${isOver ? ' day-group--drop-over' : ''}`}
      data-date={dayKey}
    >
      {children}
    </div>
  )
}

/**
 * One day in the Upcoming list: day-of-month + weekday label + task rows for
 * that date. Memoized so expand/collapse in a different day doesn't ripple
 * through every DayGroup on the page.
 */
const DayGroupComponent: React.FC<DayGroupProps> = ({
  date,
  tasks,
  todayDateKey,
  editingTaskId,
  selectedTaskId,
  isCreating,
  onStartEdit,
  onCancelEdit,
  onNavigateEdit,
  onSelectTask,
  onStartCreate,
  onCancelCreate,
  onSubmitCreate,
  onToggleComplete,
  onDelete,
  onUpdateTask,
  onDuplicate,
  onContextMenuBadgeUpdate,
}) => {
  const { i18n } = useTranslation()
  const locale = i18n.language?.startsWith('de') ? 'de-DE' : 'en-US'

  const entryData = useMemo(() => tasks.map(taskToEntryData), [tasks])
  const weekday = useMemo(
    () => date.toLocaleDateString(locale, { weekday: 'long' }),
    [date, locale],
  )
  const dayKey = dateKey(date)
  const isToday = dayKey === todayDateKey

  // Stable callback for TaskEntry.onSubmitEdit — inline in JSX would create
  // a new reference every render and defeat TaskEntry's React.memo.
  const handleSubmitEdit = useCallback(
    (id: string, data: TaskCreationData) => {
      onUpdateTask(id, data)
      onCancelEdit()
    },
    [onUpdateTask, onCancelEdit],
  )

  const handleStartCreate = useCallback(() => {
    onStartCreate(dayKey)
  }, [onStartCreate, dayKey])

  const handleSubmitCreate = useCallback(
    (data: TaskCreationData) => {
      onSubmitCreate(dayKey, data)
    },
    [onSubmitCreate, dayKey],
  )

  return (
    <DayDropZone
      dayKey={dayKey}
      className={`day-group${isToday ? ' day-group--today' : ''}`}
    >
      <div className="day-group__header">
        <span className="day-group__day">{date.getDate()}</span>
        <Dot className="day-group__dot" />
        <span className="day-group__weekday">{weekday}</span>
        {/* Hover-only "+" affordance: click opens an inline TaskEntry
            creator for this day (startDate pre-filled). Hidden while a
            creator for this day is already open. */}
        {!isCreating && (
          <button
            type="button"
            className="day-group__add"
            onClick={handleStartCreate}
            aria-label="Aufgabe an diesem Tag hinzufügen"
          >
            <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>

      {/* Always render the container so AnimatePresence stays mounted when
          the LAST task of the day exits. Without this wrapper mount/unmount
          cycle would cut the exit animation short. Empty state is handled
          via SCSS `:not(:empty)` so no stray padding leaks through. */}
      <div className="day-group__tasks">
        {/* Exit: height collapse + opacity fade, with `marginBottom: -2`
            cancelling the flex-gap to the next sibling so there's no
            unmount-time snap. No `layout` prop on the row — framer-motion
            would otherwise run layout animations on newly mounted rows
            when the user switches months, making them slide in from the
            top. */}
        <AnimatePresence initial={false}>
          {entryData.map(task => {
              const isSelected = selectedTaskId === task.id
              const isEditing = editingTaskId === task.id
              return (
                <motion.div
                  key={task.id}
                  data-task-id={task.id}
                  initial={false}
                  // height collapses the row, marginBottom: -2 cancels the
                  // flex-gap to the next sibling DURING exit so the A↔C
                  // distance stays 2px throughout — no unmount-time jump.
                  // `overflow: hidden` is only applied during exit so the
                  // collapsing height doesn't spill content; at idle the
                  // wrapper stays `overflow: visible` so TaskEntry's
                  // hover/selected `box-shadow: var(--shadow-xs)` renders
                  // — otherwise the wrapper would clip it.
                  exit={{ opacity: 0, height: 0, marginTop: 0, marginBottom: -2, overflow: 'hidden' }}
                  transition={{ duration: 0.24, ease: [0.4, 0, 0.2, 1] }}
                >
                  <DraggableTaskEntry
                    taskId={task.id}
                    mode={isEditing ? 'expanded' : 'collapsed'}
                    task={task}
                    selected={isSelected}
                    // Surrounding day list already shows the absolute date, so
                    // the collapsed badge uses relative text ("In 3 Tagen")
                    // for future dates instead of the weekday abbreviation.
                    startDateLabelMode="relative"
                    // Intentionally NOT forwarding `focused` — TaskEntry's internal
                    // scrollIntoView({ block: 'nearest' }) fires whenever `focused`
                    // flips to true, which lands the row hard against the viewport
                    // edge (inside the fade overlay). The page-level keyboard
                    // handler in UpcomingPage owns scroll positioning instead.
                    focused={false}
                    onToggleComplete={onToggleComplete}
                    onDelete={onDelete}
                    onDuplicate={onDuplicate}
                    onContextMenuBadgeUpdate={onContextMenuBadgeUpdate}
                    onClick={onSelectTask}
                    onDoubleClick={onStartEdit}
                    onSubmitEdit={handleSubmitEdit}
                    onCancel={onCancelEdit}
                    // Tab / Shift+Tab in expanded mode → jump to adjacent task.
                    // Only wire these on the currently-editing row so collapsed
                    // rows keep stable undefined handlers (React.memo friendly).
                    onNavigateNext={isEditing ? () => onNavigateEdit(task.id, 'next') : undefined}
                    onNavigatePrev={isEditing ? () => onNavigateEdit(task.id, 'prev') : undefined}
                  />
                </motion.div>
              )
          })}
        </AnimatePresence>

        {/* Inline creator — only rendered when this day is the target.
            `autoFocus` puts the cursor in the title input immediately so
            the user can start typing without another click. */}
        {isCreating && (
          <div className="day-group__creator">
            <TaskEntry
              mode="creating"
              onSubmitCreate={handleSubmitCreate}
              onCancel={onCancelCreate}
              autoFocus
            />
          </div>
        )}
      </div>
    </DayDropZone>
  )
}

export const DayGroup = React.memo(DayGroupComponent)
